import { Navigate } from "react-router-dom";
import { DialogsProvider } from "@/context/DialogsContext";
import SEOPageLayout from "@/components/seo/SEOPageLayout";
import QuoteFormDialog from "@/components/forms/QuoteFormDialog";
import { useSEO } from "@/hooks/useSEO";
import { resolvePlace, getNearbyCities } from "@/data/seo/cities";
import { getPhase1MainServices } from "@/data/seo/mainServices";
import { ROUTES } from "@/data/seo/routes";
import { WHY_US_ITEMS, FAQ_ITEMS } from "@/constants/business";
import { localBusinessSchema, serviceSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

export default function CityPage({ citySlug }) {
  const city = resolvePlace(citySlug);

  const path = `/transport-${citySlug}`;
  const title = city ? `Transport & déménagement à ${city.name} — Express Delivery` : "";
  const description = city
    ? `Express Delivery organise vos transports, déménagements et livraisons à ${city.name} : camion avec chauffeur, meubles, électroménager, achats Leboncoin / Marketplace, devis clair et rapide.`
    : "";
  const faqSubset = FAQ_ITEMS.slice(0, 5);
  const nearby = city ? getNearbyCities(city, 6) : [];
  const relatedRoutes = city
    ? ROUTES.filter((r) => r.isPhase1 && (r.fromSlug === city.slug || r.toSlug === city.slug))
    : [];

  const breadcrumbItems = city ? [{ label: "Accueil", href: "/" }, { label: city.name }] : [];

  useSEO({
    title,
    description,
    path,
    isIndexable: !!city?.isPhase1,
    jsonLd: city
      ? [
          localBusinessSchema(),
          serviceSchema({ name: `Transport à ${city.name}`, description, url: path }),
          faqSchema(faqSubset),
          breadcrumbSchema(breadcrumbItems),
        ]
      : null,
  });

  if (!city) return <Navigate to="/404" replace />;

  const relatedGroups = [];
  if (nearby.length > 0) {
    relatedGroups.push({
      title: `Villes proches de ${city.name}`,
      items: nearby.map((c) => ({ label: c.name, href: `/transport-${c.slug}` })),
    });
  }
  if (relatedRoutes.length > 0) {
    relatedGroups.push({
      title: "Trajets fréquents",
      items: relatedRoutes.map((r) => ({
        label: `${r.fromSlug === city.slug ? city.name : r.fromSlug} → ${r.toSlug === city.slug ? city.name : r.toSlug}`,
        href: `/transport-${r.slug}`,
      })),
    });
  }

  return (
    <DialogsProvider>
      <SEOPageLayout
        testIdPrefix="city-page"
        breadcrumbItems={breadcrumbItems}
        eyebrow={`Zone d'intervention • ${city.department || "France"}`}
        h1={`Transport & déménagement à ${city.name}`}
        intro={`Basé au Havre, Express Delivery intervient à ${city.name} pour tous vos besoins de transport : meubles, électroménager, achats Leboncoin / Marketplace, déménagements et débarras. Camion avec chauffeur et aide à la manutention possible selon vos besoins.`}
        serviceLinks={getPhase1MainServices().map((s) => ({ label: s.label, href: `/${s.slug}` }))}
        trustItems={WHY_US_ITEMS.slice(0, 6).map((i) => i.text)}
        relatedGroups={relatedGroups}
        faqItems={faqSubset}
        ctaProfile="particulier"
      />
      <QuoteFormDialog />
    </DialogsProvider>
  );
}
